// ErrorBoundary.js
import { Button } from "@mui/material";
import { Component, ReactNode } from "react";
import { defaultGap } from "../../styles/consts";
import { FullStack } from "../styles/Box.styles";
import { HeadingText } from "../styles/Text.styles";

type ErrorBoundaryProps = {
  children: ReactNode;
  showBackButton?: boolean;
  fallback?: (error: Error | null, reset: () => void) => ReactNode;
  onError?: (error: Error, errorInfo: unknown) => void;
};

type ErrorBoundaryState = {
  hasError: boolean;
  error: Error | null;
};

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: unknown) {
    console.error("Uncaught error:", error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return <>{this.props.fallback(this.state.error, this.reset)}</>;
      }
      return (
        <FullStack
          sx={{
            justifyContent: "center",
            alignItems: "center",
            gap: defaultGap,
          }}
        >
          <HeadingText>{"Sorry, we've encountered an error"}</HeadingText>
          {this.props.showBackButton && (
            <Button
              variant="primary"
              onClick={() => {
                window.location.href = "/";
              }}
            >
              Back to the main page
            </Button>
          )}
        </FullStack>
      );
    }

    return <>{this.props.children}</>;
  }
}

export default ErrorBoundary;
